import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RollType,
  InstrumentClass,
  type RollInput,
  type BrokerProfile,
  type Exchange,
  type ImpliedVolatility,
  validateRollInput,
  calculateRollNetCreditDebit,
  selectSchedule,
  computeCommission,
} from '@covered-calls/shared';
import { apiClient, ApiClientError } from '../services/api-client';
import { getImpliedVolatility } from '../services/market-data-service';

/**
 * RollDialog component for applying rolling strategies to covered call positions.
 *
 * Validates: Requirements 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7
 */

export interface RollDialogProps {
  positionId: string;
  currentStrike: number;
  currentExpiry: string;
  ticker: string;
  /** The exchange the underlying trades on; keys the commission schedule selection. */
  exchange: Exchange;
  /** Share quantity for the position; drives per-unit and option-contract commission math. */
  quantity: number;
  onSuccess: () => void;
  onCancel: () => void;
  initialValues?: {
    newStrikePrice: number;
    newExpiryDate: string;
    newPremium: number;
    buybackCost: number;
  };
  /**
   * Pre-selects the roll type (e.g. when opened from a strategy recommendation
   * that has no concrete economics yet). When `initialValues` are also present,
   * the roll type inferred from those values takes precedence.
   */
  initialRollType?: RollType;
}

interface FormState {
  rollType: RollType;
  buybackCost: string;
  newStrikePrice: string;
  newPremium: string;
  newExpiryDate: string;
  buybackCommission: string;
  saleCommission: string;
  brokerProfileId: string;
}

const ROLL_TYPE_LABELS: Record<RollType, string> = {
  [RollType.UP]: 'Roll Up',
  [RollType.DOWN]: 'Roll Down',
  [RollType.OUT]: 'Roll Out',
  [RollType.UP_AND_OUT]: 'Roll Up and Out',
};

const ROLL_TYPE_DESCRIPTIONS: Record<RollType, string> = {
  [RollType.UP]: 'Close current option, sell new at higher strike (same expiry)',
  [RollType.DOWN]: 'Close current option, sell new at lower strike (same expiry)',
  [RollType.OUT]: 'Close current option, sell new at same strike (later expiry)',
  [RollType.UP_AND_OUT]: 'Close current option, sell new at higher strike (later expiry)',
};

const emptyFormState: FormState = {
  rollType: RollType.UP,
  buybackCost: '',
  newStrikePrice: '',
  newPremium: '',
  newExpiryDate: '',
  buybackCommission: '',
  saleCommission: '',
  brokerProfileId: '',
};

export function RollDialog({
  positionId,
  currentStrike,
  currentExpiry,
  ticker,
  exchange,
  quantity,
  onSuccess,
  onCancel,
  initialValues,
  initialRollType,
}: RollDialogProps) {
  const getInitialFormState = (): FormState => {
    // No numeric values (opened from a recommendation): default the radio to the
    // recommended roll type when supplied, so the user starts on the right action.
    if (!initialValues) {
      return initialRollType
        ? { ...emptyFormState, rollType: initialRollType }
        : emptyFormState;
    }
    // Determine roll type from values
    let rollType = RollType.UP;
    if (initialValues.newStrikePrice === currentStrike) {
      rollType = RollType.OUT;
    } else if (initialValues.newStrikePrice > currentStrike && initialValues.newExpiryDate !== currentExpiry) {
      rollType = RollType.UP_AND_OUT;
    } else if (initialValues.newStrikePrice > currentStrike) {
      rollType = RollType.UP;
    } else if (initialValues.newStrikePrice < currentStrike) {
      rollType = RollType.DOWN;
    }
    return {
      rollType,
      buybackCost: initialValues.buybackCost > 0 ? initialValues.buybackCost.toString() : '',
      newStrikePrice: initialValues.newStrikePrice.toString(),
      newPremium: initialValues.newPremium.toString(),
      newExpiryDate: initialValues.newExpiryDate,
      buybackCommission: '',
      saleCommission: '',
      brokerProfileId: '',
    };
  };
  const [form, setForm] = useState<FormState>(getInitialFormState());
  // When the dialog is opened from a specific recommendation or economics row,
  // the roll strategy is already chosen — lock it and hide the other options so
  // the user just fills in the numbers for that action.
  const rollTypeLocked = initialRollType !== undefined || initialValues !== undefined;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [noActiveOptionError, setNoActiveOptionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>([]);
  const [impliedVolatility, setImpliedVolatility] = useState<ImpliedVolatility | null>(null);
  const [ivLoading, setIvLoading] = useState(false);
  const [ivError, setIvError] = useState<string | null>(null);

  // Fetch broker profiles on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchProfiles() {
      try {
        const profiles = await apiClient.get<BrokerProfile[]>('/broker-profiles');
        if (!cancelled) {
          setBrokerProfiles(profiles);
        }
      } catch {
        // Non-critical: profiles just won't be available for selection
      }
    }
    fetchProfiles();
    return () => { cancelled = true; };
  }, []);

  // Fetch IV when ticker + new strike + new expiry are all filled
  useEffect(() => {
    const strike = parseFloat(form.newStrikePrice);
    const expiry = form.newExpiryDate;

    if (!ticker || isNaN(strike) || strike <= 0 || !expiry) {
      setImpliedVolatility(null);
      setIvError(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    async function fetchIV() {
      setIvLoading(true);
      setIvError(null);
      try {
        const iv = await getImpliedVolatility(
          ticker,
          { strike, expiry },
          { signal: controller.signal },
        );
        if (!cancelled) {
          setImpliedVolatility(iv);
        }
      } catch (error) {
        if (!cancelled) {
          if (error instanceof ApiClientError && error.status === 0 && error.message === 'Request was cancelled.') {
            return; // Abort — ignore
          }
          setIvError('Implied volatility data is currently unavailable');
          setImpliedVolatility(null);
        }
      } finally {
        if (!cancelled) {
          setIvLoading(false);
        }
      }
    }

    // Debounce the IV fetch
    const timer = setTimeout(fetchIV, 500);
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [ticker, form.newStrikePrice, form.newExpiryDate]);

  // Calculate net credit/debit in real time
  const netCreditDebit = useMemo(() => {
    const buyback = parseFloat(form.buybackCost);
    const premium = parseFloat(form.newPremium);
    const buybackComm = parseFloat(form.buybackCommission);
    const saleComm = parseFloat(form.saleCommission);

    if (isNaN(buyback) || isNaN(premium) || isNaN(buybackComm) || isNaN(saleComm)) {
      return null;
    }

    return calculateRollNetCreditDebit(
      premium * quantity,
      buyback * quantity,
      buybackComm,
      saleComm,
    );
  }, [form.buybackCost, form.newPremium, form.buybackCommission, form.saleCommission, quantity]);

  // Additional capital headroom unlocked by moving the strike. Rolling UP raises
  // the price at which shares can be called away, so (newStrike - previousStrike)
  // x quantity is the EXTRA gain realised on the shares IF the stock reaches the
  // new strike and is assigned. This is conditional upside (only if assigned),
  // distinct from the certain option-leg net credit/debit above. Zero for Roll
  // Out (same strike); negative for Roll Down (lowering the cap).
  const strikeHeadroom = useMemo(() => {
    const newStrike =
      form.rollType === RollType.OUT ? currentStrike : parseFloat(form.newStrikePrice);
    if (isNaN(newStrike)) {
      return null;
    }
    return (newStrike - currentStrike) * quantity;
  }, [form.rollType, form.newStrikePrice, currentStrike, quantity]);

  // Auto-populate commissions when broker profile selected
  const handleBrokerProfileChange = useCallback((profileId: string) => {
    setForm((prev) => {
      const updated = { ...prev, brokerProfileId: profileId };

      if (profileId) {
        const profile = brokerProfiles.find((p) => p.profileId === profileId);
        if (profile) {
          // Both legs of a roll are OPTIONS on the position's exchange. Mirror
          // the trade-service rollOption semantics: the buyback leg's trade
          // value is buybackCost × quantity and the sale leg's trade value is
          // newStrikePrice × quantity. The stored commission is the effective
          // total (commission + GST) from the shared calculator.
          const schedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
          if (schedule) {
            const buyback = parseFloat(prev.buybackCost);
            const newStrike =
              prev.rollType === RollType.OUT
                ? currentStrike
                : parseFloat(prev.newStrikePrice);

            if (!isNaN(buyback) && buyback > 0) {
              const buybackComm = computeCommission(schedule, {
                tradeValue: buyback * quantity,
                quantity,
              });
              updated.buybackCommission = buybackComm.total.toFixed(2);
            }
            if (!isNaN(newStrike) && newStrike > 0) {
              const saleComm = computeCommission(schedule, {
                tradeValue: newStrike * quantity,
                quantity,
              });
              updated.saleCommission = saleComm.total.toFixed(2);
            }
          }
        }
      }

      return updated;
    });
  }, [brokerProfiles, exchange, quantity, currentStrike]);

  const handleFieldChange = useCallback((field: keyof FormState, value: string) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      // When switching roll type, lock the appropriate field
      if (field === 'rollType') {
        if (value === RollType.OUT) {
          updated.newStrikePrice = currentStrike.toString();
        }
        if (value === RollType.UP || value === RollType.DOWN) {
          updated.newExpiryDate = currentExpiry;
        }
      }
      return updated;
    });
    // Clear field error on change
    setFieldErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
    setServerError(null);
  }, [currentStrike]);

  const buildRollInput = (): RollInput => ({
    rollType: form.rollType,
    buybackCost: parseFloat(form.buybackCost) || 0,
    newStrikePrice: form.rollType === RollType.OUT ? currentStrike : (parseFloat(form.newStrikePrice) || 0),
    newPremium: parseFloat(form.newPremium) || 0,
    newExpiryDate: (form.rollType === RollType.UP || form.rollType === RollType.DOWN) ? currentExpiry : form.newExpiryDate,
    buybackCommission: parseFloat(form.buybackCommission) || 0,
    saleCommission: parseFloat(form.saleCommission) || 0,
    brokerProfileId: form.brokerProfileId || undefined,
  });

  const validate = (): boolean => {
    const input = buildRollInput();
    const errors = validateRollInput(input, currentStrike, currentExpiry);

    if (errors.length === 0) {
      setFieldErrors({});
      return true;
    }

    const errorMap: Record<string, string> = {};
    for (const err of errors) {
      errorMap[err.field] = err.message;
    }
    setFieldErrors(errorMap);
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    setNoActiveOptionError(null);

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const input = buildRollInput();
      await apiClient.post(`/trades/${encodeURIComponent(positionId)}/roll`, input);
      onSuccess();
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (error.status === 409) {
          // No active option exists
          setNoActiveOptionError(
            error.message || 'No open option exists to roll. The position may not have an active option contract.'
          );
        } else if (error.status === 400 && error.errors) {
          const errorMap: Record<string, string> = {};
          for (const err of error.errors) {
            errorMap[err.field] = err.message;
          }
          setFieldErrors(errorMap);
        } else {
          setServerError(error.message || 'Failed to apply rolling strategy. Please try again.');
        }
      } else {
        setServerError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-labelledby="roll-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="roll-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Roll Option — {ticker}
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Current strike: ${currentStrike.toFixed(2)} | Expiry: {currentExpiry}
        </p>

        {noActiveOptionError && (
          <div
            role="alert"
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            {noActiveOptionError}
          </div>
        )}

        {serverError && (
          <div
            role="alert"
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Roll Type Selection */}
          <fieldset style={{ border: 'none', padding: 0, margin: '0 0 1.5rem 0' }}>
            <legend style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Roll Type</legend>
            {rollTypeLocked ? (
              // Strategy pre-selected from a recommendation/economics row — show it
              // read-only rather than offering the other options.
              <div
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px',
                  backgroundColor: 'var(--color-brand-fill)',
                  border: '1px solid var(--color-brand)',
                }}
              >
                <span style={{ fontWeight: 500 }}>{ROLL_TYPE_LABELS[form.rollType]}</span>
                <br />
                <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                  {ROLL_TYPE_DESCRIPTIONS[form.rollType]}
                </span>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {Object.values(RollType).map((type) => (
                  <label
                    key={type}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      backgroundColor: form.rollType === type ? 'var(--color-brand-fill)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="rollType"
                      value={type}
                      checked={form.rollType === type}
                      onChange={() => handleFieldChange('rollType', type)}
                      style={{ marginTop: '0.25rem', minHeight: 'auto' }}
                    />
                    <div>
                      <span style={{ fontWeight: 500 }}>{ROLL_TYPE_LABELS[type]}</span>
                      <br />
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                        {ROLL_TYPE_DESCRIPTIONS[type]}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {/* Buyback Cost */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="roll-buyback-cost" style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem' }}>
              Buyback Cost ($ per share)
            </label>
            <input
              id="roll-buyback-cost"
              type="number"
              step="0.01"
              min="0"
              value={form.buybackCost}
              onChange={(e) => handleFieldChange('buybackCost', e.target.value)}
              aria-invalid={!!fieldErrors.buybackCost}
              aria-describedby={fieldErrors.buybackCost ? 'roll-buyback-cost-error' : undefined}
              style={{
                width: '100%',
                borderColor: fieldErrors.buybackCost ? 'var(--color-danger)' : 'var(--color-input-border)',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: 'var(--color-input-bg)',
                color: 'var(--color-text)',
              }}
            />
            {fieldErrors.buybackCost && (
              <p id="roll-buyback-cost-error" style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                {fieldErrors.buybackCost}
              </p>
            )}
          </div>

          {/* New Strike Price */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="roll-new-strike" style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem' }}>
              New Strike Price ($)
            </label>
            <input
              id="roll-new-strike"
              type="number"
              step="0.01"
              min="0.01"
              value={form.rollType === RollType.OUT ? currentStrike.toString() : form.newStrikePrice}
              onChange={(e) => handleFieldChange('newStrikePrice', e.target.value)}
              readOnly={form.rollType === RollType.OUT}
              aria-invalid={!!fieldErrors.newStrikePrice}
              aria-describedby={fieldErrors.newStrikePrice ? 'roll-new-strike-error' : undefined}
              style={{
                width: '100%',
                borderColor: fieldErrors.newStrikePrice ? 'var(--color-danger)' : 'var(--color-input-border)',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: form.rollType === RollType.OUT ? 'var(--color-surface-2)' : 'var(--color-input-bg)',
                color: 'var(--color-text)',
              }}
            />
            {fieldErrors.newStrikePrice && (
              <p id="roll-new-strike-error" style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                {fieldErrors.newStrikePrice}
              </p>
            )}
          </div>

          {/* New Premium */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="roll-new-premium" style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem' }}>
              New Premium ($ per share)
            </label>
            <input
              id="roll-new-premium"
              type="number"
              step="0.01"
              min="0"
              value={form.newPremium}
              onChange={(e) => handleFieldChange('newPremium', e.target.value)}
              aria-invalid={!!fieldErrors.newPremium}
              aria-describedby={fieldErrors.newPremium ? 'roll-new-premium-error' : undefined}
              style={{
                width: '100%',
                borderColor: fieldErrors.newPremium ? 'var(--color-danger)' : 'var(--color-input-border)',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: 'var(--color-input-bg)',
                color: 'var(--color-text)',
              }}
            />
            {fieldErrors.newPremium && (
              <p id="roll-new-premium-error" style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                {fieldErrors.newPremium}
              </p>
            )}
          </div>

          {/* New Expiry Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="roll-new-expiry" style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem' }}>
              New Expiry Date
            </label>
            <input
              id="roll-new-expiry"
              type="date"
              value={(form.rollType === RollType.UP || form.rollType === RollType.DOWN) ? currentExpiry : form.newExpiryDate}
              onChange={(e) => handleFieldChange('newExpiryDate', e.target.value)}
              readOnly={form.rollType === RollType.UP || form.rollType === RollType.DOWN}
              aria-invalid={!!fieldErrors.newExpiryDate}
              aria-describedby={fieldErrors.newExpiryDate ? 'roll-new-expiry-error' : undefined}
              style={{
                width: '100%',
                borderColor: fieldErrors.newExpiryDate ? 'var(--color-danger)' : 'var(--color-input-border)',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: (form.rollType === RollType.UP || form.rollType === RollType.DOWN) ? 'var(--color-surface-2)' : 'var(--color-input-bg)',
                color: 'var(--color-text)',
              }}
            />
            {fieldErrors.newExpiryDate && (
              <p id="roll-new-expiry-error" style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                {fieldErrors.newExpiryDate}
              </p>
            )}
          </div>

          {/* Broker Profile Selector */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="roll-broker-profile" style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem' }}>
              Broker Profile (optional)
            </label>
            <select
              id="roll-broker-profile"
              value={form.brokerProfileId}
              onChange={(e) => handleBrokerProfileChange(e.target.value)}
              style={{
                width: '100%',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                backgroundColor: 'var(--color-input-bg)',
                color: 'var(--color-text)',
              }}
            >
              <option value="">Manual entry</option>
              {brokerProfiles.map((profile) => (
                <option key={profile.profileId} value={profile.profileId}>
                  {profile.brokerName} ({profile.schedules.length} schedule{profile.schedules.length !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          </div>

          {/* Buyback Commission */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="roll-buyback-commission" style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem' }}>
              Buyback Commission ($)
            </label>
            <input
              id="roll-buyback-commission"
              type="number"
              step="0.01"
              min="0"
              value={form.buybackCommission}
              onChange={(e) => handleFieldChange('buybackCommission', e.target.value)}
              aria-invalid={!!fieldErrors.buybackCommission}
              aria-describedby={fieldErrors.buybackCommission ? 'roll-buyback-commission-error' : undefined}
              style={{
                width: '100%',
                borderColor: fieldErrors.buybackCommission ? 'var(--color-danger)' : 'var(--color-input-border)',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: 'var(--color-input-bg)',
                color: 'var(--color-text)',
              }}
            />
            {fieldErrors.buybackCommission && (
              <p id="roll-buyback-commission-error" style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                {fieldErrors.buybackCommission}
              </p>
            )}
          </div>

          {/* Sale Commission */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="roll-sale-commission" style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem' }}>
              Sale Commission ($)
            </label>
            <input
              id="roll-sale-commission"
              type="number"
              step="0.01"
              min="0"
              value={form.saleCommission}
              onChange={(e) => handleFieldChange('saleCommission', e.target.value)}
              aria-invalid={!!fieldErrors.saleCommission}
              aria-describedby={fieldErrors.saleCommission ? 'roll-sale-commission-error' : undefined}
              style={{
                width: '100%',
                borderColor: fieldErrors.saleCommission ? 'var(--color-danger)' : 'var(--color-input-border)',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: 'var(--color-input-bg)',
                color: 'var(--color-text)',
              }}
            />
            {fieldErrors.saleCommission && (
              <p id="roll-sale-commission-error" style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                {fieldErrors.saleCommission}
              </p>
            )}
          </div>

          {/* Implied Volatility Display */}
          <div
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--color-brand-fill)',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Implied Volatility: </span>
            {ivLoading && <span style={{ color: 'var(--color-muted)', fontSize: '0.875rem' }}>Loading...</span>}
            {!ivLoading && impliedVolatility && typeof impliedVolatility.value === 'number' && (
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {impliedVolatility.value.toFixed(2)}%
              </span>
            )}
            {!ivLoading && ivError && (
              <span style={{ color: 'var(--color-muted)', fontSize: '0.875rem' }}>{ivError}</span>
            )}
            {!ivLoading && !impliedVolatility && !ivError && (
              <span style={{ color: 'var(--color-muted)', fontSize: '0.875rem' }}>
                Enter new strike and expiry to view IV
              </span>
            )}
          </div>

          {/* Net Credit/Debit Display */}
          <div
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: netCreditDebit !== null
                ? netCreditDebit >= 0 ? 'var(--color-chip-success-bg)' : 'var(--color-chip-danger-bg)'
                : 'var(--color-surface-2)',
              borderRadius: '4px',
              marginBottom: '1.5rem',
            }}
          >
            <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Net Credit/Debit: </span>
            {netCreditDebit !== null ? (
              <span
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: netCreditDebit >= 0 ? 'var(--color-chip-success-text)' : 'var(--color-chip-danger-text)',
                }}
              >
                {netCreditDebit >= 0 ? '+' : ''}${netCreditDebit.toFixed(2)}
                {netCreditDebit >= 0 ? ' (credit)' : ' (debit)'}
              </span>
            ) : (
              <span style={{ color: 'var(--color-muted)', fontSize: '0.875rem' }}>
                Fill in buyback cost, new premium, and commissions to calculate
              </span>
            )}
          </div>

          {/* Added strike headroom — conditional upside from moving the strike.
              Only shown when it is non-zero (i.e. a strike change), so Roll Out
              does not display an irrelevant $0.00. */}
          {strikeHeadroom !== null && strikeHeadroom !== 0 && (
            <div
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'var(--color-surface-2)',
                borderRadius: '4px',
                marginBottom: '1.5rem',
              }}
            >
              <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                Added upside if assigned at new strike:{' '}
              </span>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: strikeHeadroom >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)',
                }}
              >
                {strikeHeadroom >= 0 ? '+' : ''}${strikeHeadroom.toFixed(2)}
              </span>
              <p style={{ color: 'var(--color-muted)', fontSize: '0.8rem', margin: '0.35rem 0 0' }}>
                Extra gain on your {quantity.toLocaleString()} shares from the higher strike
                (${(strikeHeadroom / quantity).toFixed(2)}/share), realised only if the stock
                reaches the new strike and the shares are called away. Weigh this conditional
                upside against the certain net {netCreditDebit !== null && netCreditDebit < 0 ? 'debit' : 'credit/debit'} above.
              </p>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-surface-2)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-brand)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Applying...' : 'Apply Roll'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
